import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getActiveProjectMutationKey } from '.';
import { projectService } from '../../application/ProjectService';
import { ProjectActionType } from '../../context/project/actions';
import { useProjectContext } from '../../context/project/context';
import { Project } from '../../domain/project/Project';
import { ProjectCustomerSMSTypes } from '../../domain/project/ProjectDTO';
import { ProjectCustomerSMSItem } from '../../domain/project/projectCustomerSMS/ProjectCustomerSMSItem';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseUpdateProjectCommunicationSMSProps {
  smsType: ProjectCustomerSMSTypes;
  smsData: {
    enabled: boolean;
    body?: string;
    include_customers_url?: boolean;
  };
}

type UseUpdateProjectCommunicationSMSResponse = ServiceResponse<
  (props: UseUpdateProjectCommunicationSMSProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useUpdateProjectCommunicationSMS = ({
  project,
}: {
  project: Project;
}): UseUpdateProjectCommunicationSMSResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const queryClient = useQueryClient();

  const { projectDispatch } = useProjectContext();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ smsType, smsData }: UseUpdateProjectCommunicationSMSProps): Promise<Project> => {
      project.customer_sms[smsType] = ProjectCustomerSMSItem.create({
        ...smsData,
        body: smsData.body ?? null,
      });
      return projectService.updateCommunicationSMS(project);
    },
    // TODO: OPTIMISTIC UPDATE!
    {
      onSuccess: (updatedProject) => {
        projectDispatch({
          type: ProjectActionType.SET_ACTIVE_PROJECT,
          payload: { activeProject: updatedProject },
        });
        queryClient.invalidateQueries(getActiveProjectMutationKey());
      },
      onError: (err: any) => {
        console.info(`Update Communication SMS Error:`, err);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: ({ smsType, smsData }: UseUpdateProjectCommunicationSMSProps) => mutate({ smsType, smsData }),
  };
};
